import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import BlogService from '../../../services/BlogService';
import './css.css';
import defaultBlogImage from '../../../assets/images/iStock-1367980878.jpg';

// Categories for blog posts
const CATEGORIES = {
  'sox': 'SOX Compliance',
  'audit': 'IT Audit',
  'automation': 'Automation',
  'compliance': 'Compliance',
  'technology': 'Technology'
};

// Helper function to determine category based on title or content
const determineCategory = (post) => {
  const title = post.title.toLowerCase();
  const content = post.content.toLowerCase();
  
  if (title.includes('sox') || content.includes('sarbanes-oxley') || content.includes('sox compliance')) {
    return 'sox';
  } else if (title.includes('audit') || content.includes('audit')) {
    return 'audit';
  } else if (title.includes('automation') || content.includes('automation') || 
             title.includes('ai') || content.includes('artificial intelligence')) {
    return 'automation';
  } else if (title.includes('compliance') || content.includes('compliance')) {
    return 'compliance';
  }
  
  return 'technology';
};

// Get initials for author avatar
const getInitials = (name) => {
  if (!name) return '?';
  return name
    .split(' ')
    .map(part => part.charAt(0))
    .join('')
    .toUpperCase()
    .substring(0, 2);
};

const BlogPage = () => {
  const [blogPosts, setBlogPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredPosts, setFilteredPosts] = useState([]);

  useEffect(() => {
    const fetchBlogPosts = async () => {
      try {
        const posts = await BlogService.getBlogPosts();
        
        // Enhance posts with category
        const enhancedPosts = posts.map(post => ({
          ...post,
          category: determineCategory(post)
        }));
        
        setBlogPosts(enhancedPosts);
        setFilteredPosts(enhancedPosts);
        setLoading(false);
      } catch (err) {
        setError('Failed to load blog posts. Please try again later.');
        setLoading(false);
      }
    };

    fetchBlogPosts();
  }, []);

  useEffect(() => {
    if (searchTerm.trim() === '') {
      setFilteredPosts(blogPosts);
    } else {
      const lowercasedSearch = searchTerm.toLowerCase();
      const filtered = blogPosts.filter(post => 
        post.title.toLowerCase().includes(lowercasedSearch) ||
        post.summary.toLowerCase().includes(lowercasedSearch) ||
        post.content.toLowerCase().includes(lowercasedSearch) ||
        post.author.toLowerCase().includes(lowercasedSearch)
      );
      setFilteredPosts(filtered);
    }
  }, [searchTerm, blogPosts]);

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleImageError = (e) => {
    e.target.src = defaultBlogImage;
  };

  if (loading) {
    return (
      <div className="blog-page-container">
        <div className="blog-header">
          <h1>AuditAI Blog</h1>
          <p>Loading blog posts...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="blog-page-container">
        <div className="blog-header">
          <h1>AuditAI Blog</h1>
          <p className="error-message">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="blog-page-container">
      <div className="blog-header">
        <h1>AuditAI Blog</h1>
        <p>Insights and best practices for audit automation and compliance</p>
      </div>
      
      <div className="blog-search-container">
        <div className="blog-search-icon">
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
            <path d="M11.742 10.344a6.5 6.5 0 1 0-1.397 1.398h-.001c.03.04.062.078.098.115l3.85 3.85a1 1 0 0 0 1.415-1.414l-3.85-3.85a1.007 1.007 0 0 0-.115-.1zM12 6.5a5.5 5.5 0 1 1-11 0 5.5 5.5 0 0 1 11 0z"/>
          </svg>
        </div>
        <input
          type="text"
          className="blog-search-input"
          placeholder="Search articles..."
          value={searchTerm}
          onChange={handleSearchChange}
        />
      </div>
      
      {filteredPosts.length === 0 ? (
        <div className="blog-no-results">
          <h3>No blog posts found</h3>
          <p>Try adjusting your search or check back later for new content.</p>
        </div>
      ) : (
        <div className="blog-posts-grid">
          {filteredPosts.map(post => (
            <div key={post.id} className="blog-post-card">
              <div className="blog-post-image">
                <img 
                  src={post.featured_image || defaultBlogImage} 
                  alt={post.title}
                  onError={handleImageError}
                />
              </div>
              <div className="blog-post-content">
                <div className="blog-post-category">{CATEGORIES[post.category]}</div>
                <h2>{post.title}</h2>
                <p className="blog-post-summary">{post.summary}</p>
                <div className="blog-post-meta">
                  <div className="author">
                    <div className="author-avatar">{getInitials(post.author)}</div>
                    <span>{post.author}</span>
                  </div>
                  <span>{new Date(post.published_date).toLocaleDateString()}</span>
                </div>
                <Link to={`/blog/${post.slug}`} className="read-more-button">
                  Read Article
                </Link>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default BlogPage; 